import { Building2, Sprout, Coffee, Package, GraduationCap, Wrench, Users, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";

const capabilities = [
  { icon: Coffee, title: "Specialty & Commercial Roasting", desc: "Small-batch and large-scale roasting on modern precision equipment." },
  { icon: Package, title: "B2B Coffee Supply", desc: "Reliable nationwide distribution for cafés, hotels, offices & institutions." },
  { icon: Sprout, title: "Private Label Manufacturing", desc: "Your brand, our beans — end-to-end contract manufacturing." },
  { icon: Wrench, title: "Equipment Sales & Leasing", desc: "Commercial espresso machines, grinders, and full café setups." },
  { icon: GraduationCap, title: "Barista Training", desc: "SCA-informed skill development for your team, delivered on-site or at our lab." },
  { icon: Building2, title: "Café Design & Consulting", desc: "Menu, layout, ops, and profitability — we help you build a café that pays back." },
  { icon: Users, title: "Corporate Beverage Solutions", desc: "Turnkey coffee programs for offices, co-working, and enterprise clients." },
  { icon: TrendingUp, title: "Plantation Experiences", desc: "Estate tours, cupping sessions, and roasting workshops for teams & travellers." },
];

const whyUs = [
  "Farm-to-Cup Traceability",
  "Premium Estate-Sourced Coffee",
  "Customized Coffee Solutions",
  "Modern Roasting Technology",
  "Sustainable & Ethical Sourcing",
  "Reliable Nationwide Distribution",
  "Dedicated Customer Support",
  "End-to-End Coffee Expertise",
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-32 pb-24">
      {/* Hero */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 mb-24">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-4">About SIGA Coffee</div>
        <h1 className="font-serif-display text-5xl md:text-7xl font-light leading-[0.95] max-w-5xl">
          Farm to Cup.<br />
          Crafted for India.<br />
          <em className="not-italic text-[#C85A32]">Trusted by businesses.</em>
        </h1>
        <p className="mt-8 max-w-3xl text-lg text-[#4A3B32] leading-relaxed">
          SIGA Coffee is the flagship coffee brand of{" "}
          <span className="text-[#1C3326] font-medium">Explora E-Trades Pvt. Ltd.</span>{" "}
          Headquartered in Bengaluru, we provide complete
          coffee solutions — from sourcing premium Arabica and Robusta from our estates in
          Chikmagalur, Sakleshpur, and The Nilgiris, to roasting and processing in{" "}
          <span className="text-[#1C3326] font-medium">Chikkamagaluru</span>, blending, packaging,
          equipment supply, café consulting, and nationwide B2B distribution.
        </p>
      </section>

      {/* Farm to cup visual */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 mb-24">
        <div className="md:col-span-7 aspect-[16/10] overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
            alt="Coffee estate at harvest"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="md:col-span-5 md:pt-12 space-y-6 text-[#4A3B32] leading-relaxed">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32]">Who we serve</div>
          <h2 className="font-serif-display text-3xl md:text-4xl font-light text-[#1C3326] leading-tight">
            Cafés · Hotels · Restaurants · Offices · Institutions · Retail
          </h2>
          <p>
            Today, SIGA Coffee delivers consistency in every cup for hundreds of businesses across
            India. Whether you're building your first café, refreshing an existing menu, or scaling
            an enterprise beverage program — we bring the beans, the machines, the training, and
            the ongoing support to make it profitable.
          </p>
          <p>
            Our expertise extends beyond coffee. We help you establish{" "}
            <span className="text-[#1C3326]">profitable coffee programs</span> through customized
            blends, commercial equipment, barista training, preventive maintenance, café setup, and
            operational support.
          </p>
        </div>
      </section>

      {/* Philosophy quote block */}
      <section className="bg-[#1C3326] text-[#FDFBF7] py-24 md:py-32 grain relative overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 relative z-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-6">Our Philosophy</div>
          <p className="font-serif-display text-3xl md:text-5xl font-light leading-tight max-w-4xl">
            "Exceptional coffee begins at the source. Every bean is carefully selected,
            professionally roasted, and rigorously quality-tested to preserve its natural aroma
            and character."
          </p>
          <p className="mt-10 max-w-3xl text-[#EBE3D5] text-lg leading-relaxed">
            Our mission is to bridge the gap between Indian coffee growers and consumers — creating
            long-term value for farmers, businesses, and coffee lovers alike.
          </p>
        </div>
      </section>

      {/* What we do — capability grid */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-4">What we do</div>
            <h2 className="font-serif-display text-4xl md:text-6xl font-light leading-[0.95]">
              End-to-end<br />coffee expertise.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-[#4A3B32] text-lg leading-relaxed">
              Eight core service pillars, one integrated partner. Whether you need beans, machines,
              barista talent, or a full café rollout — we do all of it in-house.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#EBE3D5] border border-[#EBE3D5]">
          {capabilities.map((c) => (
            <div
              key={c.title}
              data-testid={`capability-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="bg-[#FDFBF7] p-8 group hover:bg-[#F4EFE6] transition-colors"
            >
              <c.icon size={22} strokeWidth={1.4} className="text-[#C85A32] mb-6" />
              <div className="font-serif-display text-xl text-[#1C3326] leading-tight mb-3">
                {c.title}
              </div>
              <p className="text-sm text-[#4A3B32] leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why choose SIGA */}
      <section className="bg-[#F4EFE6] py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-4">Why choose SIGA</div>
          <h2 className="font-serif-display text-4xl md:text-5xl font-light leading-tight max-w-3xl">
            Eight reasons cafés, hotels & offices choose us.
          </h2>
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyUs.map((r, i) => (
              <div key={r} className="border-t border-[#1C3326] pt-5">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-3">
                  0{i + 1}
                </div>
                <div className="font-serif-display text-2xl text-[#1C3326] leading-tight">{r}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="border border-[#EBE3D5] p-10 md:p-14 bg-[#FDFBF7]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-6">Our Vision</div>
          <p className="font-serif-display text-2xl md:text-3xl font-light text-[#1C3326] leading-snug">
            To become India's most trusted integrated coffee solutions company — delivering
            world-class coffee experiences while empowering growers and businesses across the value chain.
          </p>
        </div>
        <div className="border border-[#EBE3D5] p-10 md:p-14 bg-[#1C3326] text-[#FDFBF7]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-6">Our Mission</div>
          <p className="font-serif-display text-2xl md:text-3xl font-light leading-snug">
            To provide exceptional coffee, innovative beverage solutions, and professional services
            through sustainable sourcing, technological excellence, and customer-centric partnerships.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 pb-8">
        <div className="border-t border-[#EBE3D5] pt-16 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-3">
              Partner with SIGA
            </div>
            <h3 className="font-serif-display text-3xl md:text-4xl font-light text-[#1C3326]">
              Ready to build a coffee program that pays back?
            </h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/business"
              data-testid="about-b2b-cta"
              className="bg-[#1C3326] hover:bg-[#15261C] text-[#FDFBF7] px-8 py-4 uppercase text-xs tracking-[0.3em] transition-colors"
            >
              Business Enquiry
            </Link>
            <Link
              to="/shop"
              className="border border-[#1C3326] text-[#1C3326] hover:bg-[#1C3326] hover:text-[#FDFBF7] px-8 py-4 uppercase text-xs tracking-[0.3em] transition-colors"
            >
              Shop Retail
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
